/**
 * Canonical USCIS marriage-AOS packet order.
 *
 * Mailed top-to-bottom, this is the sequence USCIS clerks expect
 * when receiving a stapled adjustment-of-status packet:
 *
 *   G-1145  (cover — opt-in electronic-notification request)
 *   I-130   (petition for alien relative — the petitioner files)
 *   I-130A  (supplemental — only when the beneficiary is a spouse)
 *   I-485   (the AOS application itself)
 *   I-765   (employment authorization)
 *   I-131   (advance parole / travel document)
 *   I-864   (affidavit of support)
 *
 * Admin can reorder by editing this constant. Forms NOT in the
 * canonical order sort to the end of the merge (by alphabetical
 * fallback), which is harmless because we only generate the seven
 * forms listed here today.
 */
import type { FormId } from '@inside-us-aos/pdf-engine';

export const CANONICAL_PACKET_ORDER: readonly FormId[] = [
  'g-1145',
  'i-130',
  'i-130a',
  'i-485',
  'i-765',
  'i-131',
  'i-864',
] as const;

const ORDER_INDEX: ReadonlyMap<FormId, number> = new Map(
  CANONICAL_PACKET_ORDER.map((id, i) => [id, i] as const),
);

/**
 * Returns a new array sorted by the canonical packet order. Forms
 * not in `CANONICAL_PACKET_ORDER` land at the end in their original
 * relative order (stable sort).
 */
export function sortByCanonicalOrder<T extends { formId: FormId }>(
  forms: readonly T[],
): T[] {
  return [...forms].sort(
    (a, b) =>
      (ORDER_INDEX.get(a.formId) ?? Number.MAX_SAFE_INTEGER) -
      (ORDER_INDEX.get(b.formId) ?? Number.MAX_SAFE_INTEGER),
  );
}
